import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';

import { EllipsisText, formatDateTime } from '../Assessments/shared';
import { listScenarioDrafts } from './api';
import { readableError } from './errors';
import { asOption, useModuleFilters } from './filters';
import {
  DraftMintSummary,
  DraftPreviewOutcome,
  ScenarioDraftSummary,
} from './types';

/**
 * Draft lifecycle tag. ScenarioDraftStatus is a different vocabulary from the
 * assessment's draft/active pair, so the shared ScenarioStatusTag cannot be
 * reused; the palette follows the same conventions (green = shippable,
 * default = parked).
 */
export const DraftStatusTag: React.FC<{ status: string }> = ({ status }) => {
  const color =
    status === 'export_ready'
      ? 'green'
      : status === 'reviewed'
        ? 'blue'
        : status === 'archived'
          ? 'default'
          : 'gold';
  return <Tag color={color}>{status.replace('_', ' ').toUpperCase()}</Tag>;
};

/** created_family / refreshed_in_place from the record's `import` key. */
export const mintResultLabel = (mint: DraftMintSummary | null) => {
  if (!mint || !mint.result) return null;
  return mint.result === 'created_family'
    ? 'NEW FAMILY'
    : mint.result === 'refreshed_in_place'
      ? 'REFRESHED'
      : String(mint.result).toUpperCase();
};

/**
 * What the newest dry run said about this draft, in one cell.
 *
 * The Mint button looks identical on a draft that will mint and one that will
 * refuse, so finding the workable drafts in a catalogue of 145 meant spending
 * a dry run on each. The ledger already knows: every preview anybody has run
 * is recorded with its reason code, and this is that answer, on the row.
 *
 * "Never previewed" is deliberately its own state and deliberately not
 * hopeful. It is not evidence the draft will mint; it is evidence nobody has
 * asked.
 */
export const mintReadiness = (
  preview: DraftPreviewOutcome | null | undefined,
): { label: string; color: string; detail: string } => {
  if (!preview) {
    return {
      label: 'NEVER PREVIEWED',
      color: 'default',
      detail:
        'Nobody has run a dry run on this draft, so nothing is known ' +
        'about whether it mints.',
    };
  }
  const status = String(preview.status || '').toUpperCase();
  if (status === 'SUCCEEDED') {
    return {
      label: 'MINTS CLEANLY',
      color: 'green',
      detail:
        'The last dry run rendered this draft and resolved every ' +
        'asset. It reflects the draft as it was then.',
    };
  }
  if (status === 'REFUSED') {
    return {
      label: `REFUSES: ${preview.code || 'a named check'}`,
      color: 'gold',
      detail:
        preview.message ||
        'A named check stopped the last dry run ' +
          'before anything was written.',
    };
  }
  return {
    label: `LAST RUN ${status}`,
    color: status === 'FAILED' ? 'red' : 'default',
    detail:
      preview.message ||
      'The last dry run did not finish. Nothing is ' +
        'known about whether this draft mints.',
  };
};

/** Newest first, and a row with no date sorts to the end rather than the top —
 *  "never" is not "recently". */
const byDate = (left?: string | null, right?: string | null) =>
  String(left || '').localeCompare(String(right || ''));

type Props = {
  onOpen: (id: number) => void;
};

const Drafts: React.FC<Props> = ({ onOpen }) => {
  const [rows, setRows] = useState<ScenarioDraftSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // In the address bar, not in component state: opening a draft unmounts this
  // whole tab strip, and a filter that dies on the way to a detail page has
  // to be retyped every time.
  const [filters, setFilters] = useModuleFilters('drafts', {
    q: '',
    status: '',
    readiness: '',
  });
  const query = filters.q;
  const status = asOption(filters.status);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await listScenarioDrafts({ q: query || undefined, status }));
    } catch (requestError) {
      setError(readableError(requestError, { what: 'The drafts list' }));
    } finally {
      setLoading(false);
    }
  }, [query, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: any[] = [
    {
      title: 'Draft',
      dataIndex: 'name',
      fixed: 'left',
      width: 260,
      render: (_: string, row: ScenarioDraftSummary) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={row.name} placement="topLeft">
            <Typography.Link
              className="assessment-ellipsis"
              onClick={() => onOpen(row.id)}
            >
              {row.name}
            </Typography.Link>
          </Tooltip>
          <EllipsisText code value={`#${row.id}`} />
        </Space>
      ),
    },
    {
      title: 'Window',
      width: 190,
      className: 'assessment-cell-nowrap',
      render: (_: unknown, row: ScenarioDraftSummary) => (
        <span>
          {row.window_start} – {row.window_end}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      sorter: (a: ScenarioDraftSummary, b: ScenarioDraftSummary) =>
        String(a.status).localeCompare(String(b.status)),
      render: (value: string) => <DraftStatusTag status={value} />,
    },
    {
      title: 'Will it mint?',
      width: 220,
      sorter: (a: ScenarioDraftSummary, b: ScenarioDraftSummary) =>
        mintReadiness(a.last_preview).label.localeCompare(
          mintReadiness(b.last_preview).label,
        ),
      render: (_: unknown, row: ScenarioDraftSummary) => {
        const readiness = mintReadiness(row.last_preview);
        return (
          <Tooltip title={readiness.detail} placement="topLeft">
            <Tag color={readiness.color} data-testid="draft-readiness">
              {readiness.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Curation',
      width: 130,
      render: (_: unknown, row: ScenarioDraftSummary) =>
        row.curated ? (
          <Tag color="green">{row.verdict_count} VERDICTS</Tag>
        ) : (
          <Tag>UNCURATED</Tag>
        ),
    },
    {
      title: 'Minted',
      width: 110,
      render: (_: unknown, row: ScenarioDraftSummary) =>
        row.minted ? (
          <Tag color="green">
            {row.mint_count != null ? `${row.mint_count} MINTS` : 'MINTED'}
          </Tag>
        ) : (
          <Tag>NOT MINTED</Tag>
        ),
    },
    {
      title: 'Last mint',
      width: 240,
      sorter: (a: ScenarioDraftSummary, b: ScenarioDraftSummary) =>
        byDate(a.last_mint?.minted_at, b.last_mint?.minted_at),
      render: (_: unknown, row: ScenarioDraftSummary) => {
        const mint = row.last_mint;
        if (!mint) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        const result = mintResultLabel(mint);
        return (
          <Space direction="vertical" size={0}>
            <Space size={4} wrap className="assessment-tag-group">
              {mint.version != null && <Tag>v{mint.version}</Tag>}
              {mint.tag && <Tag>{mint.tag}</Tag>}
              {result && (
                <Tag color={result === 'NEW FAMILY' ? 'blue' : 'purple'}>
                  {result}
                </Tag>
              )}
            </Space>
            <span className="assessment-cell-nowrap">
              {formatDateTime(mint.minted_at)}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome_code',
      width: 130,
      render: (value: string) => <EllipsisText code value={value} />,
    },
    {
      title: 'Captured',
      dataIndex: 'created_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      defaultSortOrder: 'descend',
      sorter: (a: ScenarioDraftSummary, b: ScenarioDraftSummary) =>
        byDate(a.created_at, b.created_at),
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 100,
      render: (_: unknown, row: ScenarioDraftSummary) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onOpen(row.id)}
        >
          Open
        </Button>
      ),
    },
  ];

  const visibleRows = rows.filter(row => {
    if (!filters.readiness) return true;
    const status = String(row.last_preview?.status || '').toUpperCase();
    if (filters.readiness === 'clean') return status === 'SUCCEEDED';
    if (filters.readiness === 'refuses') return status === 'REFUSED';
    return !row.last_preview;
  });

  return (
    <Card
      title="Scenario drafts"
      extra={
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Space className="assessment-filter-row" wrap>
        <Input.Search
          allowClear
          placeholder="Search name or outcome code"
          style={{ width: 340 }}
          defaultValue={query}
          onSearch={value => setFilters({ q: value })}
        />
        <Select
          allowClear
          aria-label="Draft lifecycle state"
          placeholder="All lifecycle states"
          style={{ width: 190 }}
          value={status}
          onChange={value => setFilters({ status: value || '' })}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'reviewed', label: 'Reviewed' },
            { value: 'export_ready', label: 'Export ready' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
        <Select
          allowClear
          aria-label="Mint readiness"
          placeholder="Ready to mint or not"
          style={{ width: 230 }}
          value={asOption(filters.readiness)}
          onChange={value => setFilters({ readiness: value || '' })}
          options={[
            { value: 'clean', label: 'Mints cleanly' },
            { value: 'refuses', label: 'Refuses' },
            { value: 'unknown', label: 'Never previewed' },
          ]}
        />
      </Space>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Table
        rowKey="id"
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={visibleRows}
        columns={columns}
        scroll={{ x: 1680 }}
        pagination={{
          // `defaultPageSize`, never `pageSize`: a controlled page size with
          // no onChange made the "100 / page" picker decorative — it snapped
          // straight back to 20 and served 20 rows.
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100, 200],
          showTotal: total =>
            `${total} draft${total === 1 ? '' : 's'}${
              total === rows.length ? '' : ` of ${rows.length}`
            }`,
        }}
      />
    </Card>
  );
};

export default Drafts;
